/**
 * TimeInput24h - 24-hour format time input with shorthand support
 * 
 * Features:
 * - Accepts 24-hour format (18:00)
 * - Accepts shorthand (1800 → auto-formats to 18:00)
 * - Accepts 12-hour input (6pm → converts to 18:00)
 * - Validates on blur
 * - Shows formatted value
 */
import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../components/ui/input';
import { parseTimeInput } from '../lib/timeparse';

export default function TimeInput24h({
  value,
  onChange,
  placeholder = 'HH:MM',
  className = '',
  disabled = false,
  id,
  name,
  'data-testid': dataTestId,
  ...props
}) {
  const [displayValue, setDisplayValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  // Sync external value to display
  useEffect(() => {
    if (!isEditing && value) {
      const parsed = parseTimeInput(value);
      setDisplayValue(parsed || value);
    }
  }, [value, isEditing]);

  const handleFocus = () => {
    setIsEditing(true);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setDisplayValue(raw);
    
    // Try to parse on the fly for valid inputs
    const parsed = parseTimeInput(raw);
    if (parsed && onChange) {
      // Only emit change if fully valid
      // But don't auto-format while typing
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    
    if (!displayValue) {
      onChange && onChange('');
      return;
    }
    
    // Parse and format on blur
    const parsed = parseTimeInput(displayValue);
    if (parsed) {
      setDisplayValue(parsed);
      onChange && onChange(parsed);
    } else {
      // Invalid input - reset to previous value or show error
      if (value) {
        setDisplayValue(value);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={`font-mono ${className}`}
      disabled={disabled}
      id={id}
      name={name}
      data-testid={dataTestId}
      autoComplete="off"
      {...props}
    />
  );
}
